import { createSlice } from '@reduxjs/toolkit';
import { dispatch } from '../store';
import { updateError } from './user';
import { callApi } from '../../utils/apiCall';
import { hasFeatureAccess } from '../../utils/featurePermission';

// -----------------------------------------------

const initialState = {
  activeStep: 0,
  campaignData: {
    name: '',
    channel: 'EMAIL',
    campaignType: 'STANDARD',
    templateId: null,
    whatsappTemplateId: null,
    whatsappPhoneId: null,
    audienceId: null,
    recipientFilters: null,
    scheduledAt: null,
    sendNow: true,
    tags: [],
    additionalContext: {},
    sequenceMessages: [],
    practiceSequenceId: null,
  },
  // UI state
  isLoading: false,
  isSaving: false,
  isSubmitting: false,
  submitSuccess: false,
  submitError: null,
  launchedCampaignId: null,
  templateSearch: '',
  templateCategory: 'ALL',
  audienceTab: 'existing',
  // Data fetched from API
  templates: [],
  templatesLoading: false,
  audiences: [],
  audiencesLoading: false,
  whatsappTemplates: [],
  whatsappTemplatesLoading: false,
  whatsappPhones: [],
  whatsappPhonesLoading: false,
  recipientCount: null,
  recipientCountLoading: false,
  whatsappLimits: null,
  // Edit mode
  editCampaignId: null,
  editCampaignLoading: false,
};

const slice = createSlice({
  name: 'campaignBuilder',
  initialState,
  reducers: {
    resetBuilder(state) {
      Object.assign(state, initialState);
    },
    setActiveStep(state, action) {
      state.activeStep = action.payload;
    },
    updateCampaignData(state, action) {
      state.campaignData = {
        ...state.campaignData,
        ...action.payload,
      };
    },
    setTemplateSearch(state, action) {
      state.templateSearch = action.payload;
    },
    setTemplateCategory(state, action) {
      state.templateCategory = action.payload;
    },
    setAudienceTab(state, action) {
      state.audienceTab = action.payload;
    },
    setEditCampaign(state, action) {
      const campaign = action.payload;
      state.editCampaignId = campaign.id;
      state.campaignData = {
        name: campaign.name || '',
        channel: campaign.channel || 'EMAIL',
        campaignType: campaign.campaign_type || 'STANDARD',
        templateId: campaign.engagement_template?.id || campaign.engagement_template || null,
        whatsappTemplateId: campaign.whatsapp_template || null,
        whatsappPhoneId: campaign.whatsapp_phone?.id || campaign.whatsapp_phone || null,
        audienceId: campaign.audience?.audience?.id || campaign.audience?.audience || null,
        recipientFilters: campaign.recepient?.filters || null,
        scheduledAt: campaign.scheduled_at || null,
        sendNow: !campaign.scheduled_at,
        tags: campaign.tags || [],
        additionalContext: campaign.additional_context || {},
        sequenceMessages: [],
        practiceSequenceId: campaign.practice_sequence || null,
      };
      state.editCampaignLoading = false;
    },
    addSequenceMessage(state) {
      const msgs = state.campaignData.sequenceMessages;
      const lastDelay = msgs.length > 0 ? msgs[msgs.length - 1].delay + 604800 : 0;
      msgs.push({
        localId: `msg_${Date.now()}_${msgs.length}`,
        subject: '',
        body: '',
        delay: lastDelay,
        channel: state.campaignData.channel || 'EMAIL',
        use_unlayer: true,
        unlayer_template: {},
        unlayer_html: '',
        is_enabled: true,
      });
    },
    removeSequenceMessage(state, action) {
      state.campaignData.sequenceMessages = state.campaignData.sequenceMessages.filter(
        (m) => m.localId !== action.payload
      );
    },
    updateSequenceMessage(state, action) {
      const { localId, ...updates } = action.payload;
      const msg = state.campaignData.sequenceMessages.find((m) => m.localId === localId);
      if (msg) {
        Object.assign(msg, updates);
      }
    },
    reorderSequenceMessages(state, action) {
      state.campaignData.sequenceMessages = action.payload;
    },
    // Loading states
    setTemplatesLoading(state, action) {
      state.templatesLoading = action.payload;
    },
    setTemplates(state, action) {
      state.templates = action.payload;
      state.templatesLoading = false;
    },
    setAudiencesLoading(state, action) {
      state.audiencesLoading = action.payload;
    },
    setAudiences(state, action) {
      state.audiences = action.payload;
      state.audiencesLoading = false;
    },
    setWhatsappTemplatesLoading(state, action) {
      state.whatsappTemplatesLoading = action.payload;
    },
    setWhatsappTemplates(state, action) {
      state.whatsappTemplates = action.payload;
      state.whatsappTemplatesLoading = false;
    },
    setWhatsappPhonesLoading(state, action) {
      state.whatsappPhonesLoading = action.payload;
    },
    setWhatsappPhones(state, action) {
      state.whatsappPhones = action.payload;
      state.whatsappPhonesLoading = false;
    },
    setRecipientCount(state, action) {
      state.recipientCount = action.payload;
      state.recipientCountLoading = false;
    },
    setRecipientCountLoading(state, action) {
      state.recipientCountLoading = action.payload;
    },
    setIsSubmitting(state, action) {
      state.isSubmitting = action.payload;
    },
    setIsSaving(state, action) {
      state.isSaving = action.payload;
    },
    setSubmitSuccess(state, action) {
      state.submitSuccess = action.payload;
    },
    setLaunchedCampaignId(state, action) {
      state.launchedCampaignId = action.payload;
    },
    setSubmitError(state, action) {
      state.submitError = action.payload;
    },
    setEditCampaignLoading(state, action) {
      state.editCampaignLoading = action.payload;
    },
    setWhatsappLimits(state, action) {
      state.whatsappLimits = action.payload;
    },
  },
});

export default slice.reducer;

export const {
  resetBuilder,
  setActiveStep,
  updateCampaignData,
  setTemplateSearch,
  setTemplateCategory,
  setAudienceTab,
  setEditCampaign,
  addSequenceMessage,
  removeSequenceMessage,
  updateSequenceMessage,
  reorderSequenceMessages,
} = slice.actions;

// ---- Thunks ----

export function fetchTemplateGallery(practiceId) {
  return async () => {
    if (!practiceId) return;
    try {
      dispatch(slice.actions.setTemplatesLoading(true));
      const response = await callApi(
        `/api/v1/practices/${practiceId}/template-gallery/?ordering=-created_at&limit=100`
      );
      if (response.status === 200) {
        dispatch(slice.actions.setTemplates(response.data?.results || []));
      } else {
        dispatch(slice.actions.setTemplates([]));
        dispatch(updateError({ success: false, message: 'Failed to load templates' }));
      }
    } catch (error) {
      dispatch(slice.actions.setTemplates([]));
      dispatch(updateError({ success: false, message: error.message || 'Failed to load templates' }));
    }
  };
}

export function fetchAudiences(practiceId) {
  return async () => {
    if (!practiceId) return;
    try {
      dispatch(slice.actions.setAudiencesLoading(true));
      const response = await callApi(
        `/api/v1/practices/${practiceId}/audiences/?ordering=-created_at&limit=100&fields={recepient_count,audience_internal_queryset,description,id,name,type,created_at}`
      );
      if (response.status === 200) {
        dispatch(slice.actions.setAudiences(response.data?.results || []));
      } else {
        dispatch(slice.actions.setAudiences([]));
        dispatch(updateError({ success: false, message: 'Failed to load audiences' }));
      }
    } catch (error) {
      dispatch(slice.actions.setAudiences([]));
      dispatch(updateError({ success: false, message: error.message || 'Failed to load audiences' }));
    }
  };
}

export function fetchWhatsappTemplates(practiceId) {
  return async () => {
    if (!practiceId) return;
    if (!hasFeatureAccess('whatsapp-messaging')) return;
    try {
      dispatch(slice.actions.setWhatsappTemplatesLoading(true));
      const accountsResp = await callApi(
        `/api/v1/practices/${practiceId}/whatsapp-business-accounts/`
      );
      const { count, results } = accountsResp?.data ?? {};
      if (count === 0) {
        dispatch(slice.actions.setWhatsappTemplates([]));
        return;
      }
      const allTemplates = await Promise.all(
        results?.map(async (account) => {
          const templateResp = await callApi(
            `/api/v1/practices/${practiceId}/whatsapp-business-accounts/${account.id}/whatsapp-message-templates/?ordering=-created_at`
          );
          return (templateResp?.data?.results || []).map((t) => ({
            ...t,
            businessName: account.name,
            businessId: account.id,
          }));
        }) || []
      );
      dispatch(slice.actions.setWhatsappTemplates(allTemplates.flat()));
    } catch (error) {
      dispatch(slice.actions.setWhatsappTemplates([]));
      dispatch(updateError({ success: false, message: 'Failed to load WhatsApp templates' }));
    }
  };
}

export function fetchWhatsappPhones(practiceId) {
  return async () => {
    if (!practiceId) return;
    if (!hasFeatureAccess('whatsapp-messaging')) return;
    try {
      dispatch(slice.actions.setWhatsappPhonesLoading(true));
      const accountsResp = await callApi(
        `/api/v1/practices/${practiceId}/whatsapp-business-accounts/`
      );
      const { results } = accountsResp?.data ?? {};
      const allPhones = await Promise.all(
        (results || []).map(async (account) => {
          const phonesResp = await callApi(
            `/api/v1/practices/${practiceId}/whatsapp-business-accounts/${account.id}/whatsapp-phones/`
          );
          return (phonesResp?.data?.results || []).map((p) => ({
            ...p,
            businessName: account.name,
            businessAccountId: account.id,
          }));
        })
      );
      dispatch(slice.actions.setWhatsappPhones(allPhones.flat()));
    } catch (error) {
      dispatch(slice.actions.setWhatsappPhones([]));
      dispatch(updateError({ success: false, message: 'Failed to load WhatsApp phones' }));
    }
  };
}

export function fetchRecipientCount(practiceId, filters) {
  return async () => {
    if (!practiceId || !filters) return;
    try {
      dispatch(slice.actions.setRecipientCountLoading(true));
      const response = await callApi(
        `/api/v1/practices/${practiceId}/campaigns/0/preview_recepients/?limit=1&offset=0`,
        'post',
        { practiceId, filters }
      );
      if (response.status === 200) {
        dispatch(slice.actions.setRecipientCount(response.data?.count ?? 0));
      } else {
        dispatch(slice.actions.setRecipientCount(null));
      }
    } catch (error) {
      dispatch(slice.actions.setRecipientCount(null));
    }
  };
}

export function createCampaignFromBuilder(practiceId, builderData, navigate, onSettled) {
  return async () => {
    if (!practiceId) return;
    try {
      dispatch(slice.actions.setIsSubmitting(true));
      dispatch(slice.actions.setSubmitError(null));

      const payload = {
        practiceId,
        name: builderData.name,
        channel: builderData.channel,
      };

      if (builderData.sendNow) {
        payload.scheduled_at = new Date(Date.now() + 30 * 1000).toISOString();
      } else if (builderData.scheduledAt) {
        payload.scheduled_at = builderData.scheduledAt;
      }

      payload.campaign_type = builderData.campaignType || 'STANDARD';

      if (builderData.channel === 'WHATSAPP') {
        payload.whatsapp_template = builderData.whatsappTemplateId;
        payload.whatsapp_phone = builderData.whatsappPhoneId;
      } else {
        payload.engagement_template = builderData.templateId;
      }

      if (builderData.audienceId) {
        payload.audience = { audience: builderData.audienceId };
      }

      if (builderData.recipientFilters) {
        payload.recepient = { filters: builderData.recipientFilters };
      }

      // NURTURE flow: create sequence + messages, then attach to campaign
      if (builderData.campaignType === 'NURTURE') {
        const seqResp = await callApi(
          `/api/v1/practices/${practiceId}/campaign-sequences/`,
          'post',
          { name: `${builderData.name} Sequence`, description: '' }
        );
        if (seqResp.status !== 201) {
          throw new Error('Failed to create sequence');
        }
        const sequenceId = seqResp.data.id;
        dispatch(slice.actions.updateCampaignData({ practiceSequenceId: sequenceId }));

        // Create messages sequentially (order matters for delay sequence)
        const createMsg = async (msgs, idx) => {
          if (idx >= msgs.length) return undefined;
          const msg = msgs[idx];
          const msgResp = await callApi(
            `/api/v1/practices/${practiceId}/campaign-sequences/${sequenceId}/messages/`,
            'post',
            {
              subject: msg.subject,
              body: msg.body || '',
              channel: msg.channel || builderData.channel,
              delay: msg.delay,
              is_enabled: msg.is_enabled !== false,
              use_unlayer: msg.use_unlayer !== false,
              unlayer_template: msg.unlayer_template || {},
              unlayer_html: msg.unlayer_html || '',
            }
          );
          if (msgResp.status !== 201) {
            throw new Error(`Failed to create message: ${msg.subject}`);
          }
          return createMsg(msgs, idx + 1);
        };
        await createMsg(builderData.sequenceMessages, 0);

        payload.practice_sequence = sequenceId;
        payload.campaign_type = 'NURTURE';
        delete payload.engagement_template;
        delete payload.whatsapp_template;
        delete payload.whatsapp_phone;
      }

      const response = await callApi(
        `/api/v1/practices/${practiceId}/campaigns/`,
        'post',
        payload
      );

      if (response.status === 201) {
        const campaignId = response.data?.id;

        if (builderData.sendNow && campaignId) {
          const scheduleResp = await callApi(
            `/api/v1/practices/${practiceId}/campaigns/${campaignId}/schedule/`,
            'post'
          );
          if (scheduleResp.status >= 400) {
            dispatch(slice.actions.setSubmitError(
              `Campaign created but failed to schedule: ${JSON.stringify(scheduleResp.data) || 'Unknown error'}`
            ));
            dispatch(slice.actions.setIsSubmitting(false));
            if (onSettled) onSettled();
            return;
          }
        }

        dispatch(slice.actions.setLaunchedCampaignId(campaignId));
        dispatch(slice.actions.setSubmitSuccess(true));
        dispatch(updateError({ success: true, message: 'Campaign created successfully' }));
      } else {
        dispatch(
          slice.actions.setSubmitError(
            JSON.stringify(response.data) || 'Failed to create campaign'
          )
        );
      }
    } catch (error) {
      dispatch(
        slice.actions.setSubmitError(error.message || 'Failed to create campaign')
      );
    } finally {
      dispatch(slice.actions.setIsSubmitting(false));
      if (onSettled) onSettled();
    }
  };
}

export function updateCampaignFromBuilder(practiceId, campaignId, builderData, onSettled) {
  return async () => {
    if (!practiceId || !campaignId) return;
    try {
      dispatch(slice.actions.setIsSubmitting(true));
      dispatch(slice.actions.setSubmitError(null));

      const payload = {
        practiceId,
        name: builderData.name,
        channel: builderData.channel,
      };

      if (builderData.sendNow) {
        payload.scheduled_at = new Date(Date.now() + 30 * 1000).toISOString();
      } else if (builderData.scheduledAt) {
        payload.scheduled_at = builderData.scheduledAt;
      }

      payload.campaign_type = builderData.campaignType || 'STANDARD';

      if (builderData.channel === 'WHATSAPP') {
        payload.whatsapp_template = builderData.whatsappTemplateId;
        payload.whatsapp_phone = builderData.whatsappPhoneId;
      } else {
        payload.engagement_template = builderData.templateId;
      }

      if (builderData.audienceId) {
        payload.audience = { audience: builderData.audienceId };
        payload.recepient = null;
      }

      if (builderData.recipientFilters) {
        payload.recepient = { filters: builderData.recipientFilters };
        payload.audience = null;
      }

      const response = await callApi(
        `/api/v1/practices/${practiceId}/campaigns/${campaignId}/`,
        'put',
        payload
      );

      if (response.status === 200) {
        // Handle sequence messages for NURTURE campaigns
        if (builderData.campaignType === 'NURTURE' && builderData.practiceSequenceId) {
          const seqId = builderData.practiceSequenceId;
          const builderMessages = builderData.sequenceMessages || [];

          // Fetch current server messages
          const serverRes = await callApi(
            `/api/v1/practices/${practiceId}/campaign-sequences/${seqId}/messages/`
          );
          const serverMessages = serverRes.ok ? (serverRes.data?.results || serverRes.data || []) : [];
          const serverIds = new Set(serverMessages.map((m) => m.id));
          const builderServerIds = new Set(builderMessages.filter((m) => m.serverId).map((m) => m.serverId));

          // DELETE removed messages
          const deleteResults = await Promise.all(
            serverMessages
              .filter((msg) => !builderServerIds.has(msg.id))
              .map((msg) =>
                callApi(
                  `/api/v1/practices/${practiceId}/campaign-sequences/${seqId}/messages/${msg.id}/`,
                  'delete'
                )
              )
          );

          // CREATE new messages (no serverId)
          const createResults = await Promise.all(
            builderMessages
              .filter((msg) => !msg.serverId)
              .map((msg) =>
                callApi(
                  `/api/v1/practices/${practiceId}/campaign-sequences/${seqId}/messages/`,
                  'post',
                  {
                    subject: msg.subject,
                    body: msg.body,
                    channel: msg.channel,
                    delay: msg.delay,
                    use_unlayer: msg.use_unlayer || false,
                    unlayer_template: msg.unlayer_template,
                    unlayer_html: msg.unlayer_html,
                  }
                )
              )
          );

          // PATCH modified messages
          const patchResults = await Promise.all(
            builderMessages
              .filter((msg) => msg.serverId && serverIds.has(msg.serverId))
              .map((msg) =>
                callApi(
                  `/api/v1/practices/${practiceId}/campaign-sequences/${seqId}/messages/${msg.serverId}/`,
                  'patch',
                  {
                    subject: msg.subject,
                    body: msg.body,
                    channel: msg.channel,
                    delay: msg.delay,
                    use_unlayer: msg.use_unlayer || false,
                    unlayer_template: msg.unlayer_template,
                    unlayer_html: msg.unlayer_html,
                  }
                )
              )
          );

          // Check if any message operations failed
          const allResults = [...deleteResults, ...createResults, ...patchResults];
          const failedOps = allResults.filter((r) => r && !r.ok);
          if (failedOps.length > 0) {
            dispatch(slice.actions.setSubmitError(`Campaign saved but ${failedOps.length} message operation(s) failed`));
            dispatch(updateError({ success: false, message: `Campaign saved but ${failedOps.length} sequence message(s) failed to sync` }));
            return;
          }
        }

        // Fire the FSM transition DRAFT → SCHEDULED when the user picked
        // "Send Now" (or any future scheduled_at) on an EDIT. Without
        // this the PUT only writes the scheduled_at field but the
        // campaign stays in DRAFT and the Celery worker never picks it
        // up — mirror of the create-flow logic on line ~425.
        //
        // The backend FSM safely rejects the call if the campaign has
        // already moved past DRAFT (e.g. user re-saves an existing
        // SCHEDULED campaign). We swallow that error to avoid showing
        // a false "failed to schedule" message — the campaign is still
        // in a valid send state.
        const needsScheduleTransition = builderData.sendNow || builderData.scheduledAt;
        if (needsScheduleTransition) {
          const scheduleResp = await callApi(
            `/api/v1/practices/${practiceId}/campaigns/${campaignId}/schedule/`,
            'post'
          );
          // Only surface 4xx errors that aren't "already past DRAFT" —
          // a 400 with TransitionNotAllowed-style detail means the
          // campaign was already scheduled / sending / sent, which is
          // not a user-visible failure on save.
          if (scheduleResp.status >= 400) {
            const detail = JSON.stringify(scheduleResp.data || '');
            const isAlreadyScheduled = /transition|already.*(scheduled|sending|sent)|invalid.*state/i.test(detail);
            if (!isAlreadyScheduled) {
              dispatch(slice.actions.setSubmitError(
                `Campaign updated but failed to schedule: ${detail || 'Unknown error'}`
              ));
              dispatch(slice.actions.setIsSubmitting(false));
              if (onSettled) onSettled();
              return;
            }
          }
        }

        dispatch(slice.actions.setLaunchedCampaignId(campaignId));
        dispatch(slice.actions.setSubmitSuccess(true));
        dispatch(updateError({ success: true, message: 'Campaign updated successfully' }));
      } else {
        dispatch(
          slice.actions.setSubmitError(
            JSON.stringify(response.data) || 'Failed to update campaign'
          )
        );
      }
    } catch (error) {
      dispatch(
        slice.actions.setSubmitError(error.message || 'Failed to update campaign')
      );
    } finally {
      dispatch(slice.actions.setIsSubmitting(false));
      if (onSettled) onSettled();
    }
  };
}

export function fetchSequenceMessages(practiceId, sequenceId) {
  return async () => {
    if (!practiceId || !sequenceId) return;
    try {
      const response = await callApi(
        `/api/v1/practices/${practiceId}/campaign-sequences/${sequenceId}/`
      );
      if (response.status === 200 && response.data?.practice_messages) {
        const messages = response.data.practice_messages.map((pm) => ({
          localId: `srv_${pm.id}`,
          serverId: pm.id,
          subject: pm.subject || '',
          body: pm.body || '',
          delay: pm.delay || 0,
          channel: pm.channel || 'EMAIL',
          use_unlayer: pm.use_unlayer !== false,
          unlayer_template: pm.unlayer_template || {},
          unlayer_html: pm.unlayer_html || '',
          is_enabled: pm.is_enabled !== false,
        }));
        dispatch(slice.actions.updateCampaignData({ sequenceMessages: messages }));
      }
    } catch (error) {
      dispatch(updateError({ success: false, message: 'Failed to load sequence messages' }));
    }
  };
}

export function loadCampaignForEdit(practiceId, campaignId) {
  return async () => {
    if (!practiceId || !campaignId) return;
    try {
      dispatch(slice.actions.setEditCampaignLoading(true));
      const response = await callApi(
        `/api/v1/practices/${practiceId}/campaigns/${campaignId}/`
      );
      if (response.status === 200) {
        dispatch(slice.actions.setEditCampaign(response.data));
        dispatch(slice.actions.setEditCampaignLoading(false));
        if (response.data?.campaign_type === 'NURTURE' && response.data?.practice_sequence) {
          dispatch(fetchSequenceMessages(practiceId, response.data.practice_sequence));
        }
      } else {
        dispatch(updateError({ success: false, message: 'Failed to load campaign' }));
        dispatch(slice.actions.setEditCampaignLoading(false));
      }
    } catch (error) {
      dispatch(updateError({ success: false, message: error.message || 'Failed to load campaign' }));
      dispatch(slice.actions.setEditCampaignLoading(false));
    }
  };
}

// Send test email for STANDARD campaigns (engagement templates)
export function sendTestEmailForTemplate(practiceId, templateId, email) {
  return async () => {
    try {
      const response = await callApi(
        `/api/v1/practices/${practiceId}/engagement-templates/${templateId}/send_test/`,
        'post',
        { email }
      );
      if (response.status === 200) {
        dispatch(updateError({ success: true, message: 'Test email sent successfully' }));
        return true;
      }
      dispatch(updateError({ success: false, message: JSON.stringify(response.data) || 'Failed to send test email' }));
      return false;
    } catch (error) {
      dispatch(updateError({ success: false, message: error.message || 'Failed to send test email' }));
      return false;
    }
  };
}

export function fetchWhatsappLimits(practiceId, businessAccountId, phoneId) {
  return async () => {
    if (!practiceId || !businessAccountId || !phoneId) return;
    if (!hasFeatureAccess('whatsapp-messaging')) return;
    try {
      const response = await callApi(
        `/api/v1/practices/${practiceId}/whatsapp-business-accounts/${businessAccountId}/whatsapp-phones/${phoneId}/limits/`
      );
      if (response.ok) {
        dispatch(slice.actions.setWhatsappLimits(response.data));
      }
    } catch (error) {
      // Non-critical — silently fail
    }
  };
}

// Send test email for NURTURE campaigns (campaign sequence messages)
export function sendTestEmailForSequenceMessage(practiceId, sequenceId, messageId, email) {
  return async () => {
    try {
      const response = await callApi(
        `/api/v1/practices/${practiceId}/campaign-sequences/${sequenceId}/messages/${messageId}/send-test/`,
        'put',
        { email }
      );
      if (response.status === 200) {
        dispatch(updateError({ success: true, message: 'Test email sent successfully' }));
        return true;
      }
      dispatch(updateError({ success: false, message: JSON.stringify(response.data) || 'Failed to send test email' }));
      return false;
    } catch (error) {
      dispatch(updateError({ success: false, message: error.message || 'Failed to send test email' }));
      return false;
    }
  };
}
